/**
 * Cópia para a área de transferência, com recuo para contexto não seguro.
 *
 * `navigator.clipboard` só existe em contexto seguro — `https:` ou `localhost`.
 * Um cliente que abre o console por `http://<ip>:8080` (port-forward, o caminho
 * da instalação do dia 15/09) não tem o objeto, e os três botões «Copiar» do
 * console engoliam o erro: nada ia para a área de transferência e nada dizia isso.
 *
 * O recuo é `document.execCommand('copy')` sobre um `<textarea>` temporário. É
 * obsoleto e continua a ser o único caminho sem contexto seguro; quando nem ele
 * funciona a resposta é `false`, e quem chama mostra o recuo honesto — o valor
 * está na tela para copiar à mão.
 */
/**
 * O estado do botão «Copiar». `failed` existe porque o silêncio era o defeito:
 * sem ele o botão volta a dizer «Copiar» depois de não copiar nada.
 */
export type CopyState = 'idle' | 'done' | 'failed';

export async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Contexto seguro com permissão negada, ou documento sem foco. O recuo
      // abaixo ainda resolve os dois casos, então não se desiste aqui.
    }
  }
  return copyByExecCommand(text);
}

function copyByExecCommand(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  // Fora da vista e fora do fluxo, mas ainda selecionável: `display: none` e
  // `visibility: hidden` fazem a seleção não existir, e a cópia sai vazia.
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.left = '0';
  area.style.width = '1px';
  area.style.height = '1px';
  area.style.padding = '0';
  area.style.border = 'none';
  area.style.opacity = '0';
  document.body.appendChild(area);

  const selection = document.getSelection();
  const previous = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  try {
    area.focus();
    area.select();
    area.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
    if (previous && selection) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }
  }
}
